/**
 * Format date with smart relative display (Today, Yesterday, etc.)
 * @param {Date|string|number} dateInput - Date object, ISO string, or Unix timestamp
 * @param {Object} options - Formatting options
 * @param {boolean} options.showTime - Whether to show time (default: true)
 * @param {boolean} options.showRelative - Whether to show relative dates (default: true)
 * @param {string} options.timeZone - Timezone (default: 'Europe/Istanbul')
 * @returns {string} Formatted date string
 */
export function formatDate(dateInput, options = {}) {
  const {
    showTime = true,
    showRelative = true,
    timeZone = "Europe/Istanbul",
  } = options;

  if (!dateInput) return "";
  if (dateInput === "0001-01-01T00:00:00Z") return "";

  let date;

  // Handle different input types
  if (typeof dateInput === "number") {
    // Unix timestamp (check if it's in seconds or milliseconds)
    date = new Date(dateInput < 10000000000 ? dateInput * 1000 : dateInput);
  } else if (typeof dateInput === "string") {
    // ISO 8601 string
    date = new Date(dateInput);
  } else if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    return "";
  }

  if (isNaN(date.getTime())) return "";

  // Get current date in the same timezone
  const now = new Date();
  const nowInTimezone = new Date(now.toLocaleString("en-US", { timeZone }));
  const dateInTimezone = new Date(date.toLocaleString("en-US", { timeZone }));

  // Calculate difference in days
  const startOfToday = new Date(nowInTimezone);
  startOfToday.setHours(0, 0, 0, 0);

  const startOfDate = new Date(dateInTimezone);
  startOfDate.setHours(0, 0, 0, 0);

  const daysDiff = Math.floor(
    (startOfToday - startOfDate) / (1000 * 60 * 60 * 24)
  );

  // Format time if needed
  const timeOptions = {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  const timeStr = showTime ? date.toLocaleString("tr-TR", timeOptions) : "";

  // Show relative date names
  if (showRelative) {
    if (daysDiff === 0) {
      return showTime ? `Bugün ${timeStr}` : "Bugün";
    } else if (daysDiff === 1) {
      return showTime ? `Dün ${timeStr}` : "Dün";
    } else if (daysDiff === 2) {
      return showTime ? `2 gün önce ${timeStr}` : "2 gün önce";
    } else if (daysDiff > 2 && daysDiff <= 7) {
      return showTime
        ? `${daysDiff} gün önce ${timeStr}`
        : `${daysDiff} gün önce`;
    } else if (daysDiff === -1) {
      return showTime ? `Yarın ${timeStr}` : "Yarın";
    }
  }

  // Default format for older dates
  const dateOptions = {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  if (showTime) {
    dateOptions.hour = "2-digit";
    dateOptions.minute = "2-digit";
    dateOptions.hour12 = false;
  }

  return date.toLocaleString("tr-TR", dateOptions);
}

/**
 * Legacy function - converts Unix timestamp to Turkey time
 * @deprecated Use formatDate instead
 */
export function convertUnixToTurkeyTime(unixTimestamp) {
  return formatDate(unixTimestamp, { showRelative: false });
}

/**
 * Legacy function - converts ISO 8601 to Turkey time
 * @deprecated Use formatDate instead
 */
export function convertISO8601ToTurkeyTime(utcDate) {
  return formatDate(utcDate, { showRelative: false });
}
